import React, { useEffect, useRef, useState } from "react";
import { useKeycloak } from "@react-keycloak/web";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";

import {
  getContentPermissions,
  logUserAnalyticService,
} from "../../../../app/src/services/services";
import "./ProductsComponent.css";
import {
  turnOffLoadingActionCreator,
  turnOnLoadingActionCreator,
} from "../../../../app/src/actions/commonActions";
import { LOCAL_STORAGE_KEY__LOCALE } from "../../../../app/src/Utils/Constants";
import {
  URL_CANCEL_CHECKOUT,
  URL_SUCCESS_CHECKOUT,
} from "../../../../app/src/Components/Routes/Location";
import {
  PRODUCT_RECURRING_PERIOD,
  PRODUCT_RECURRING_TYPE,
} from "./ProductsConstants";
import { getProductCheckoutService } from "../../../../app/src/services/services";
import {
  MULTITENANT_CONSTANTS,
  TENANT,
} from "../../../../app/src/Resources/Multitenant/tenantConstants";
import {
  DETAIL_VALUE,
  PAGE_ID_KEY,
  PAGE_VIEW_ACTION,
} from "../../../../app/src/analyticsConstants/analyticsConstants";
import WithAuthorization from "../../../../app/src/Utils/WithAuthorization";
import { isCordovaAvailable } from "../../../../app/src/Utils/secureStorageUtil";
import { useHistory } from "react-router-dom";

const ProductsComponent = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  let { id } = useParams();
  const { keycloak, initialized } = useKeycloak();
  const username = keycloak?.tokenParsed?.preferred_username;

  const locale = localStorage.getItem(LOCAL_STORAGE_KEY__LOCALE);
  const tenantStyles = useSelector((state) => state.commonState.tenantStyles);

  const [productsData, setProductsData] = useState([]);
  const [checkoutURL, setCheckoutURL] = useState(null);
  const [loading, setLoading] = useState(null);
  const [checkoutError, setCheckoutError] = useState(null);
  const checkoutErrorRef = useRef(null);

  useEffect(() => {
    !!username && MULTITENANT_CONSTANTS[TENANT].analytics && logUserAnalyticService(
      TENANT,
      username,
      PAGE_VIEW_ACTION,
      PAGE_ID_KEY,
      DETAIL_VALUE,
      keycloak?.token
    );
  }, [username]);

  const turnOnLoading = () => {
    dispatch(turnOnLoadingActionCreator());
  };

  const turnOffLoading = () => {
    dispatch(turnOffLoadingActionCreator());
  };

  const getCheckoutErrorMessage = (data, status) => {
    const backendErrorCode = data?.error?.code;
    const backendErrorMessage = data?.error?.message;

    if (backendErrorCode && !data?.error?.type) {
      const backendMessages = {
        "product-missing-stripe-id": t("products.checkout.error.product_missing_stripe_id", "Este producto no está disponible para compra en este momento."),
        "missing-urls": t("products.checkout.error.missing_urls", "No se pudo iniciar el pago. Inténtalo de nuevo en unos segundos."),
        "invalid-jwt": t("products.checkout.error.invalid_jwt", "Tu sesión ha expirado. Vuelve a iniciar sesión para continuar."),
        forbidden: t("products.checkout.error.forbidden", "No tienes permisos para realizar esta compra."),
        "product-not-found": t("products.checkout.error.product_not_found", "No se encontró el producto solicitado."),
        "price-not-found": t("products.checkout.error.price_not_found", "Este producto no tiene precio configurado actualmente."),
        "billing-not-configured": t("products.checkout.error.billing_not_configured", "El servicio de pagos no está disponible temporalmente."),
        "product-error": t("products.checkout.error.product_error", "Hubo un problema consultando el producto. Inténtalo de nuevo."),
        "customer-error": t("products.checkout.error.customer_error", "No se pudo preparar el cliente de pago. Inténtalo de nuevo."),
        "price-error": t("products.checkout.error.price_error", "No se pudo obtener el precio del producto. Inténtalo de nuevo."),
        "checkout-error": t("products.checkout.error.checkout_error", "Hubo un error iniciando el checkout. Inténtalo de nuevo."),
        "checkout-session-error": t("products.checkout.error.checkout_session_error", "No se pudo crear la sesión de pago. Inténtalo de nuevo."),
        "internal-error": t("products.checkout.error.internal_error", "Ha ocurrido un error inesperado. Inténtalo de nuevo más tarde."),
        "upstream-unreachable": t("products.checkout.error.upstream_unreachable", "No se pudo conectar con el servicio de pago. Inténtalo de nuevo."),
        "upstream-timeout": t("products.checkout.error.upstream_timeout", "El servicio de pago está tardando demasiado. Inténtalo de nuevo."),
        "request-timeout": t("products.checkout.error.request_timeout", "La operación ha tardado demasiado. Inténtalo de nuevo."),
      };

      return backendMessages[backendErrorCode] || backendErrorMessage || t("products.checkout.error.generic", "No se pudo iniciar el proceso de pago. Inténtalo de nuevo.");
    }

    if (data?.error?.type) {
      const stripeType = data.error.type;
      const stripeCode = data.error.code;

      if (stripeType === "rate_limit_error" || status === 429) {
        return t("products.checkout.error.stripe_rate_limit", "Hemos alcanzado el límite de peticiones al proveedor de pago. Espera unos segundos e inténtalo de nuevo.");
      }

      if (stripeType === "authentication_error" || stripeCode === "invalid_api_key") {
        return t("products.checkout.error.stripe_auth", "El proveedor de pago no está disponible temporalmente. Inténtalo más tarde.");
      }

      if (stripeType === "permission_error") {
        return t("products.checkout.error.stripe_permission", "No se puede procesar el pago con la configuración actual. Inténtalo más tarde.");
      }

      return data?.error?.message || t("products.checkout.error.stripe_generic", "El proveedor de pago devolvió un error. Inténtalo de nuevo.");
    }

    if (Array.isArray(data?.errors)) {
      return data.errors?.[0]?.message || t("products.checkout.error.directus_generic", "No se pudo validar la información del producto. Inténtalo de nuevo.");
    }

    if (status === 504) {
      return t("products.checkout.error.timeout", "El servicio de pago está tardando demasiado. Inténtalo de nuevo.");
    }

    return t("products.checkout.error.generic", "No se pudo iniciar el proceso de pago. Inténtalo de nuevo.");
  };

  const productCheckoutCallback = (response) => {
    if (response?.ok && response?.data?.url) {
      setCheckoutURL(response.data.url);
      return;
    }

    setLoading(null);
    setCheckoutError(getCheckoutErrorMessage(response?.data, response?.status));
  };

  useEffect(() => {
    if (!!id && (!!keycloak?.token || ((!!keycloak && !!initialized) || isCordovaAvailable()))) {
      turnOnLoading();
      getContentPermissions(TENANT, id, keycloak?.token, null, (response) => {
        try {
          const errorBody = response?.error_error?.http_body
            ? JSON.parse(response.error_error.http_body)
            : null;

          const products = errorBody?.error?.products || [];

          if (products.length > 0) {
            const validProducts = products
              .filter(product => product.products_id)
              .map(product => ({
                ...product.products_id,
                productId: product.products_id.id
              }));
            setProductsData(prevProducts => [...prevProducts, ...validProducts]);
          }
        } catch (error) {
          console.error("❌ Error al procesar los permisos del contenido:", error);
        } finally {
          turnOffLoading();
        }
      });
    }
  }, [id, keycloak]);

  const handleCheckOutProduct = (productId) => {
    if (loading) {
      return;
    }

    const cancelUrl = `${window.location.origin}${URL_CANCEL_CHECKOUT}/${id}`;
    const successUrl = `${window.location.origin}${URL_SUCCESS_CHECKOUT}/${id}`;
    const body = { successUrl, cancelUrl };

    setCheckoutError(null);
    setLoading(productId);

    getProductCheckoutService(
      productId,
      MULTITENANT_CONSTANTS[TENANT].tenantServer,
      username,
      locale,
      keycloak?.token,
      body,
      productCheckoutCallback
    );
  };

  useEffect(() => {
    if (checkoutURL && loading) {
      setLoading(null);
      if (isCordovaAvailable()) {
        const browserRef = window.cordova.InAppBrowser.open(checkoutURL, "_blank", "location=no,toolbar=no");
        browserRef.addEventListener('loadstart', (event) => {
          if (event.url === `${window.location.origin}${URL_CANCEL_CHECKOUT}/${id}`) {
            browserRef.close();
            history.push(`${URL_CANCEL_CHECKOUT}/${id}`);
          } else if (event.url === `${window.location.origin}${URL_SUCCESS_CHECKOUT}/${id}`) {
            browserRef.close();
            history.push(`${URL_SUCCESS_CHECKOUT}/${id}`);
          }
        });
      } else {
        const win = window.open(checkoutURL, "_blank");
        if (win) {
          win.focus();
        } else {
          setCheckoutError(t("products.checkout.error.popup_blocked", "No hemos podido abrir la ventana de pago. Revisa si el navegador lo está bloqueando."));
        }
      }
      setCheckoutURL(null);
    }
  }, [checkoutURL]);

  useEffect(() => {
    if (!checkoutError || !checkoutErrorRef.current) {
      return;
    }

    const isCordova = isCordovaAvailable();
    const isMobileOrTablet = window.matchMedia("(max-width: 1024px)").matches;

    if (!isCordova && !isMobileOrTablet) {
      return;
    }

    const getScrollableParent = (element) => {
      let parent = element?.parentElement;

      while (parent) {
        const style = window.getComputedStyle(parent);
        const hasScrollableY = /(auto|scroll)/.test(style.overflowY);

        if (hasScrollableY && parent.scrollHeight > parent.clientHeight) {
          return parent;
        }

        parent = parent.parentElement;
      }

      return document.scrollingElement || document.documentElement;
    };

    const scrollToError = () => {
      const errorElement = checkoutErrorRef.current;

      if (!errorElement) {
        return;
      }

      const scrollContainer = getScrollableParent(errorElement);
      const offset = 75;

      if (scrollContainer === document.scrollingElement || scrollContainer === document.documentElement || scrollContainer === document.body) {
        const targetY = window.pageYOffset + errorElement.getBoundingClientRect().top - offset;
        window.scrollTo({
          top: Math.max(targetY, 0),
          behavior: isCordova ? "auto" : "smooth",
        });
        return;
      }

      const containerRect = scrollContainer.getBoundingClientRect();
      const elementRect = errorElement.getBoundingClientRect();
      const targetTop = scrollContainer.scrollTop + (elementRect.top - containerRect.top) - offset;

      try {
        scrollContainer.scrollTo({
          top: Math.max(targetTop, 0),
          behavior: isCordova ? "auto" : "smooth",
        });
      } catch (error) {
        scrollContainer.scrollTop = Math.max(targetTop, 0);
      }
    };

    const timeoutId = setTimeout(scrollToError, 100);
    const retryTimeoutId = setTimeout(scrollToError, 300);

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(retryTimeoutId);
    };
  }, [checkoutError]);

  useEffect(() => {
    if (!loading) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [loading]);

  const transformPriceNumber = (number) => {
    const result = number / 100;
    return result.toFixed(2);
  };

  const isMonthlyPayment = (product) => {
    return (
      product.recurringPeriod === PRODUCT_RECURRING_PERIOD &&
      product.recurringType === PRODUCT_RECURRING_TYPE
    );
  };

  const backgroundImageByTenant = () => {
    return `url(${tenantStyles?.backgroundRegister})`;
  };

  return (
    <div
      className={"Page"}
      style={{ backgroundImage: backgroundImageByTenant() }}
    >
      {productsData.length > 0 && (
        <div className="Container">
          <div className="PageHeader">
            <div>
              <ion-icon name="lock-open" id="LockIcon" aria-label={t("products.unlock.content")} />
            </div>
            <p className="PageTitle">{t("products.choose.pack")}</p>
          </div>
          {checkoutError && (
            <div className="CheckoutErrorBanner" role="alert" ref={checkoutErrorRef}>
              {checkoutError}
            </div>
          )}
          <div className="ProductsContainer">
            {productsData.map((product, index) => (
              <div
                className="ProductCard"
                key={`${product.productId}-${index}`}
                onClick={() => handleCheckOutProduct(product.productId)}
                style={{
                  background: `url(${product.image?.data?.url}) no-repeat center center`,
                  backgroundSize: "cover",
                }}
              >
                <div className="Gradient" />
                <div className={"groupTitleDescription"}>
                  <div className={"ProductMainInfo"}>
                  <strong className={"ProductTitle"}>{product.name}</strong>
                    <strong className={"ProductPrice"}>
                      {transformPriceNumber(product.price)}
                    </strong>
                    <strong className={"ProductCurrency"}>
                      {product.currency}{" "}
                      {isMonthlyPayment(product)
                        ? `/ ${t("products.month")}`
                        : ""}
                    </strong>
                  </div>
                  <div className={"ProductIconText"}>
                    {product.description}
                  </div>
                  <div className="PurchaseButtonContainer">
                    <button
                      className="PurchaseButton"
                      type="button"
                      disabled={loading === product.productId}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleCheckOutProduct(product.productId);
                      }}
                    >
                      {t("products.buy", "Comprar")}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {loading && (
        <div className="CheckoutLoadingOverlay" role="alertdialog" aria-modal="true" aria-live="assertive">
          <div className="CheckoutLoadingPopup">
            {t("products.checkout.connecting_gateway", "Conectando con la pasarela de pago...")}
          </div>
        </div>
      )}
    </div>
  );
};

export default WithAuthorization(ProductsComponent);
